import { useState, useEffect, useCallback } from 'react';
import KeywordChart from './KeywordChart';
import { tokenize, analyzeKeywords } from './analysisUtils';

const API_TOKEN = process.env.REACT_APP_API_TOKEN || '';

// Reusable gate for Pro/AI features — wraps any AI section with blur + upgrade CTA
export const ProGate = ({ locked, children, label, signedIn }) => {
  if (!locked) return children;
  return (
    <div style={proGateStyles.wrap}>
      <div style={proGateStyles.blurred}>{children}</div>
      <div style={proGateStyles.overlay}>
        <svg viewBox="0 0 24 24" width="24" height="24" fill="#a78bfa" style={{ marginBottom: '8px' }}>
          <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
        </svg>
        {label && <span style={proGateStyles.label}>{label}</span>}
        <span style={proGateStyles.text}>
          {signedIn ? 'Upgrade to Pro to unlock' : 'Sign in to unlock'}
        </span>
        <button onClick={() => { window.location.hash = signedIn ? '#/pricing' : '#/signin'; }} style={proGateStyles.btn}>
          {signedIn ? 'View Plans' : 'Sign In'}
        </button>
      </div>
    </div>
  );
};

const proGateStyles = {
  wrap: {
    position: 'relative', borderRadius: '12px', overflow: 'hidden',
  },
  blurred: {
    filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none',
  },
  overlay: {
    position: 'absolute', inset: 0,
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(15, 23, 42, 0.6)',
    borderRadius: '12px', zIndex: 2,
  },
  label: {
    fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
    letterSpacing: '0.5px', color: '#a78bfa', marginBottom: '4px',
  },
  text: {
    fontSize: '15px', fontWeight: '600', color: '#e2e8f0', marginBottom: '12px',
  },
  btn: {
    padding: '8px 24px', borderRadius: '8px', border: 'none',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

export const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return (
    <button onClick={handleCopy} style={styles.copyBtn} title="Copy to clipboard">
      {copied ? (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="#4ade80"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
      ) : (
        <svg viewBox="0 0 24 24" width="14" height="14" fill="#94a3b8"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
      )}
    </button>
  );
};

const extractVideoId = (input) => {
  const trimmed = input.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  try {
    const url = new URL(trimmed);
    if (url.hostname === 'youtu.be') return url.pathname.slice(1);
    return url.searchParams.get('v');
  } catch {
    return null;
  }
};


// --- Title Length Optimizer Component ---
const TITLE_CATEGORIES = [
  { key: 'curiosity', label: 'Curiosity Gap', icon: '?', color: '#f59e0b' },
  { key: 'value', label: 'Value-Driven', icon: '▶', color: '#4ade80' },
  { key: 'emotional', label: 'Emotional Hook', icon: '!', color: '#f87171' },
];

const TitleOptimizer = ({ aiTitles, isPro, signedIn }) => {
  const [draft, setDraft] = useState('');
  const len = draft.length;
  const desktopOk = len <= 70;
  const mobileOk = len <= 40;

  // Support both old format (array) and new format (object with categories)
  const isCategories = aiTitles && !Array.isArray(aiTitles) && typeof aiTitles === 'object';
  const hasAny = isCategories
    ? TITLE_CATEGORIES.some(c => aiTitles[c.key]?.length > 0)
    : Array.isArray(aiTitles) && aiTitles.length > 0;

  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Title Length Optimizer</h3>
      <p style={styles.hint}>YouTube truncates at ~70 chars (desktop) and ~40 chars (mobile)</p>
      <div style={styles.titleInputWrap}>
        <input
          type="text"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          placeholder="Draft your title here..."
          style={styles.titleInput}
          className="titler-title-input"
        />
        <span style={{ ...styles.charCount, color: !desktopOk ? '#f87171' : !mobileOk ? '#fbbf24' : '#4ade80' }}>
          {len} chars
        </span>
      </div>
      {draft && (
        <div style={styles.truncationPreview}>
          <div style={styles.previewRow}>
            <span style={styles.previewLabel}>Desktop:</span>
            <span style={styles.previewText}>
              {draft.substring(0, 70)}{len > 70 && <span style={{ color: '#64748b' }}>...</span>}
            </span>
            <span style={{ color: desktopOk ? '#4ade80' : '#f87171', fontSize: '12px', marginLeft: '8px' }}>
              {desktopOk ? 'OK' : 'Truncated'}
            </span>
          </div>
          <div style={styles.previewRow}>
            <span style={styles.previewLabel}>Mobile:</span>
            <span style={styles.previewText}>
              {draft.substring(0, 40)}{len > 40 && <span style={{ color: '#64748b' }}>...</span>}
            </span>
            <span style={{ color: mobileOk ? '#4ade80' : '#fbbf24', fontSize: '12px', marginLeft: '8px' }}>
              {mobileOk ? 'OK' : 'Truncated'}
            </span>
          </div>
        </div>
      )}
      {hasAny && (
        <ProGate locked={!isPro} label="AI Title Suggestions" signedIn={signedIn}>
        <div style={{ marginTop: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
            <span style={styles.inlineAiLabel}>AI</span>
            <p style={{ ...styles.hint, margin: 0 }}>AI-generated titles — click to load, copy to share</p>
          </div>
          {isCategories ? (
            TITLE_CATEGORIES.map(cat => {
              const titles = aiTitles[cat.key] || [];
              if (titles.length === 0) return null;
              return (
                <div key={cat.key} style={{ marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                    <span style={{
                      width: '20px', height: '20px', borderRadius: '6px',
                      background: `${cat.color}22`, color: cat.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: '700', flexShrink: 0,
                    }}>{cat.icon}</span>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#cbd5e1' }}>{cat.label}</span>
                  </div>
                  <div style={styles.titleSuggestions}>
                    {titles.map((t, i) => (
                      <div key={`${cat.key}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button onClick={() => setDraft(t)} style={{ ...styles.titleSuggestion, ...styles.aiTitleSuggestion, borderLeftColor: cat.color }} className="titler-title-suggestion">
                          {t}
                          <span style={{ color: t.length <= 70 ? '#4ade80' : '#f87171', fontSize: '11px', marginLeft: '6px' }}>
                            ({t.length})
                          </span>
                        </button>
                        <CopyButton text={t} />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={styles.titleSuggestions}>
              {aiTitles.map((t, i) => (
                <div key={`ai-${i}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button onClick={() => setDraft(t)} style={{ ...styles.titleSuggestion, ...styles.aiTitleSuggestion }} className="titler-title-suggestion">
                    {t}
                    <span style={{ color: t.length <= 70 ? '#4ade80' : '#f87171', fontSize: '11px', marginLeft: '6px' }}>
                      ({t.length})
                    </span>
                  </button>
                  <CopyButton text={t} />
                </div>
              ))}
            </div>
          )}
        </div>
        </ProGate>
      )}
    </div>
  );
};

// --- Timeline Component ---
const TopicTimeline = ({ timeline }) => {
  if (!timeline || timeline.length === 0) return null;
  const maxCount = Math.max(...timeline.flatMap(t => t.counts), 1);

  return (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Topic Timeline</h3>
      <p style={styles.hint}>When keywords appear throughout the video</p>
      <div style={styles.timelineGrid}>
        <div style={styles.timelineHeaderRow}>
          <span style={styles.timelineKeyword}></span>
          {timeline[0].labels.map(l => (
            <span key={l} style={styles.timelineHeader}>{l}</span>
          ))}
        </div>
        {timeline.map(({ keyword, counts }) => (
          <div key={keyword} style={styles.timelineRow}>
            <span style={styles.timelineKeyword}>{keyword}</span>
            {counts.map((c, i) => (
              <div key={i} style={styles.timelineCell}>
                <div style={{
                  ...styles.timelineDot,
                  opacity: c > 0 ? Math.max(0.3, c / maxCount) : 0.08,
                  transform: `scale(${c > 0 ? Math.max(0.5, c / maxCount) : 0.3})`,
                }} />
                {c > 0 && <span style={styles.timelineCellCount}>{c}</span>}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Mock AI data shown behind blur for non-Pro users
const MOCK_AI_RESULT = {
  titles: {
    curiosity: [
      'The Real Reason Nobody Talks About This',
      'I Was Wrong About Everything — Here\'s Why',
      'What Nobody Tells You About This Topic',
    ],
    value: [
      'The Ultimate Guide You Need to Watch Right Now',
      '10 Secrets That Will Transform Your Approach',
      'How to Master This in Under 30 Minutes',
    ],
    emotional: [
      'Stop Making These Mistakes — Here\'s the Fix',
      'Why This Changes Everything You Thought You Knew',
      'This One Shift Will Change Your Life Forever',
    ],
  },
  summary: 'This video provides an in-depth exploration of the topic, covering key strategies and actionable insights. The creator breaks down complex ideas into easy-to-follow steps, making it accessible for both beginners and experienced viewers.',
  sentiment: {
    overall: 'positive',
    tone: 'Educational & Motivating',
    energy: 'high',
    score: 78,
    summary: 'The overall tone is enthusiastic and encouraging, with a strong focus on providing value and actionable takeaways for the audience.',
  },
  quotes: [
    'This is the one thing that separates the best from the rest',
    'If you take nothing else from this video, remember this',
    'Most people get this completely wrong and it costs them everything',
    'The data speaks for itself — the results are undeniable',
  ],
  hooks: [
    'What if I told you everything you know about this is wrong?',
    'I spent 100 hours researching this so you don\'t have to.',
    'Stop scrolling — this one tip changed everything for me.',
  ],
  tags: ['tutorial', 'how-to', 'tips', 'guide', 'strategy', 'beginner-friendly', 'expert-advice', 'productivity', '2026', 'must-watch'],
};


const generateDescription = (keywords, videoInfo, aiResult) => {
  const lines = [];
  // Title line
  if (videoInfo?.title) lines.push(videoInfo.title);
  else lines.push('');
  lines.push('');

  // Summary
  if (aiResult?.summary) {
    lines.push(aiResult.summary);
  } else if (keywords.summary.length > 0) {
    lines.push(keywords.summary.slice(0, 3).join('. ') + '.');
  }
  lines.push('');

  // Timestamps placeholder
  lines.push('Timestamps:');
  lines.push('0:00 - Introduction');
  lines.push('[Add your timestamps here]');
  lines.push('');

  // Tags/hashtags
  if (aiResult?.tags?.length > 0) {
    lines.push(aiResult.tags.slice(0, 10).map(t => `#${t.replace(/\s+/g, '')}`).join(' '));
  } else if (keywords.phrases.length > 0) {
    lines.push(keywords.phrases.slice(0, 5).map(p => `#${p.word.replace(/\s+/g, '')}`).join(' '));
  }

  return lines.join('\n');
};

const deriveTextTitle = (kw, text) => {
  // Try a question first, then a top phrase, then first line of text
  if (kw?.questions?.length > 0) {
    const q = kw.questions[0] + '?';
    return q.length <= 25 ? q : q.substring(0, 22) + '...';
  }
  if (kw?.phrases?.length > 0) {
    const p = kw.phrases[0].word;
    return p.length <= 25 ? p : p.substring(0, 22) + '...';
  }
  if (text) {
    const firstLine = text.split(/[\n.!?]/)[0].trim();
    if (firstLine.length > 0) {
      return firstLine.length <= 25 ? firstLine : firstLine.substring(0, 22) + '...';
    }
  }
  return 'Text Analysis';
};

const YouTubeAnalyzer = ({ buildUrl, user, isPro = false, viewOnlyData, onBackToHistory, updateUser, showCreditToast }) => {
  const hasCredits = (user?.requestLimit ?? 0) > 0;
  const canUseFeatures = isPro || hasCredits;
  const deductCredit = () => {
    if (!isPro && updateUser && user) {
      updateUser({ requestLimit: Math.max((user.requestLimit ?? 0) - 1, 0) });
    }
  };
  const isViewOnly = !!viewOnlyData;
  const [mode, setMode] = useState(() => {
    if (viewOnlyData?.videoInfo?.videoId) return 'url';
    if (viewOnlyData) return 'text';
    return user ? (canUseFeatures ? 'url' : 'text') : 'text';
  }); // 'url' or 'text'
  const [url, setUrl] = useState('');
  const [manualText, setManualText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [keywords, setKeywords] = useState(viewOnlyData?.keywords || null);
  const [transcript, setTranscript] = useState(viewOnlyData?.transcript || null);
  const [videoInfo, setVideoInfo] = useState(viewOnlyData?.videoInfo || null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [aiResult, setAiResult] = useState(viewOnlyData?.aiResult || null);
  const [aiLoading, setAiLoading] = useState(false);
  const [paidForSession, setPaidForSession] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [transcriptSource, setTranscriptSource] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [recentLabel, setRecentLabel] = useState('Try it out');
  // canUseFeatures gates NEW analysis requests; showProFeatures gates display of current results
  const showProFeatures = canUseFeatures || paidForSession;

  // Fetch user's history first; fall back to curated demo videos
  useEffect(() => {
    if (!user || isViewOnly) return;
    // Try user history first
    fetch(buildUrl('/history'), {
      headers: {
        'Authorization': `Bearer ${API_TOKEN}`,
        'X-User-Email': user.email || '',
      },
    })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(data => {
        const items = (data.items || []).filter(i => i.videoId && i.analysisData && i.title);
        if (items.length > 0) {
          setRecentSearches(items.slice(0, 3));
          setRecentLabel('Recently searched');
        } else {
          // No history — show curated demo videos
          fetch(buildUrl('/recent-analyses?videoIds=pgrMDBEbjQE,Rjx8OtxDHyw,OWorFLIbRoo'))
            .then(r => r.ok ? r.json() : { items: [] })
            .then(d => { setRecentSearches(d.items || []); setRecentLabel('Try it out'); })
            .catch(() => {});
        }
      })
      .catch(() => {
        // Fallback to demo videos on error
        fetch(buildUrl('/recent-analyses?videoIds=pgrMDBEbjQE,Rjx8OtxDHyw,OWorFLIbRoo'))
          .then(r => r.ok ? r.json() : { items: [] })
          .then(d => { setRecentSearches(d.items || []); setRecentLabel('Try it out'); })
          .catch(() => {});
      });
  }, [buildUrl, user, isViewOnly]);

  const hasResults = keywords || error;

  const clearResults = () => {
    setKeywords(null);
    setTranscript(null);
    setVideoInfo(null);
    setError(null);
    setAiResult(null);
    setTranscriptSource(null);
    setLimitReached(false);
    setPaidForSession(false);
  };

  const switchMode = (newMode) => {
    if (newMode === mode) return;
    if (newMode === 'url' && !canUseFeatures) {
      window.location.hash = user ? '#/pricing' : '#/signin';
      return;
    }
    clearResults();
    setMode(newMode);
  };


  const fetchAiAnalysis = async (videoId, text, title, extraBody = {}) => {
    if (!canUseFeatures) return null;
    setAiLoading(true);
    try {
      const res = await fetch(buildUrl('/ai-analyze'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_TOKEN}`,
          'X-User-Email': user?.email || '',
        },
        body: JSON.stringify({ videoId, transcript: text, currentTitle: title || '', ...extraBody }),
      });
      if (res.ok) {
        const data = await res.json();
        setAiResult(data.analysis);
        return data.analysis;
      }
    } catch {
      // AI is optional — silently fail
    } finally {
      setAiLoading(false);
    }
    return null;
  };


  const restoreFromHistory = (item) => {
    clearResults();
    const ad = item.analysisData || {};
    if (ad.keywords) setKeywords(ad.keywords);
    if (ad.transcript) setTranscript(ad.transcript);
    if (ad.videoInfo) setVideoInfo(ad.videoInfo);
    if (ad.aiResult) setAiResult(ad.aiResult);
    if (ad.videoInfo?.videoId) {
      setUrl(`https://www.youtube.com/watch?v=${ad.videoInfo.videoId}`);
      setMode('url');
    } else {
      setManualText(ad.transcript || '');
      setMode('text');
    }
  };

  const handleAnalyzeUrl = async () => {
    const videoId = extractVideoId(url);
    if (!videoId) {
      setError('Please enter a valid YouTube URL or video ID');
      return;
    }

    setLoading(true);
    clearResults();

    try {
      // Check cache first
      if (canUseFeatures && user?.email) {
        try {
          const cacheRes = await fetch(buildUrl(`/check-cache?videoId=${encodeURIComponent(videoId)}`), {
            headers: {
              'Authorization': `Bearer ${API_TOKEN}`,
              'X-User-Email': user.email,
            },
          });
          if (cacheRes.ok) {
            const cacheData = await cacheRes.json();
            if (cacheData.found && cacheData.item?.analysisData) {
              console.log('Cache hit — restoring from history');
              restoreFromHistory(cacheData.item);
              setLoading(false);
              return;
            }
          }
        } catch { /* cache miss, continue normally */ }
      }

      const endpoint = buildUrl(`/transcript?videoId=${encodeURIComponent(videoId)}`);
      if (!endpoint) throw new Error('API not configured');

      const headers = {
        'Authorization': `Bearer ${API_TOKEN}`,
      };
      if (user?.email) {
        headers['X-User-Email'] = user.email;
      }
      const res = await fetch(endpoint, { headers });
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        setLimitReached(true);
        throw new Error(data.error || 'You have reached your request limit.');
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setTranscript(data.text);
      setTranscriptSource(data.source || 'captions');
      const vi = { videoId, title: data.title, duration: data.duration };
      setVideoInfo(vi);
      const result = analyzeKeywords(data.text);
      setKeywords(result);
      if (canUseFeatures) setPaidForSession(true);
      deductCredit(); // credit was deducted server-side in /transcript
      if (showCreditToast) showCreditToast(1);

      // History is saved server-side in /transcript; pass historyId to AI so it updates the same entry
      const serverHistoryId = data.historyId || null;
      fetchAiAnalysis(videoId, data.text, data.title, {
        historyId: serverHistoryId,
        historyData: { keywords: result, videoInfo: vi, transcript: data.text },
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyzeText = async () => {
    const trimmed = manualText.trim();
    if (trimmed.length < 50) {
      setError('Please enter at least 50 characters of text to analyze.');
      return;
    }
    setLoading(true);
    clearResults();
    try {
      setTranscript(trimmed);
      const result = analyzeKeywords(trimmed);
      setKeywords(result);
      if (canUseFeatures) setPaidForSession(true);
      if (canUseFeatures) {
        // Server saves history during /ai-analyze so it persists even if user navigates away
        const ai = await fetchAiAnalysis(null, trimmed, '', {
          historyData: {
            title: deriveTextTitle(result, trimmed),
            keywords: result,
            transcript: trimmed,
          },
        });
        if (ai) { deductCredit(); if (showCreditToast) showCreditToast(1); }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = mode === 'url' ? handleAnalyzeUrl : handleAnalyzeText;
  const noCredits = user && !isPro && !hasCredits;
  const canAnalyze = mode === 'url' ? (!noCredits && !loading && url.trim()) : (!loading && manualText.trim().length > 0);

  return (
    <div style={styles.page}>
      <style>{`
        @media (max-width: 768px) {
          .titler-centered { min-height: auto !important; padding: 24px 16px !important; }
          .titler-top { padding: 20px 16px 24px !important; }
          .titler-tagline { font-size: 13px !important; margin-bottom: 16px !important; }
          .titler-search-container { max-width: 100% !important; }
          .titler-analyze-btn { padding: 10px 20px !important; font-size: 14px !important; height: 40px !important; }
          .titler-textarea { font-size: 14px !important; min-height: 80px !important; }
          .titler-search-bar { height: 44px !important; padding: 0 14px !important; }
          .titler-search-input { font-size: 14px !important; }
        }
      `}</style>
      <div style={isViewOnly ? styles.topLayout : (hasResults ? styles.topLayout : styles.centeredLayout)}
           className={isViewOnly ? 'titler-top' : (hasResults ? 'titler-top' : 'titler-centered')}>
        {isViewOnly && onBackToHistory && (
          <div style={{ width: '100%', maxWidth: '960px', marginBottom: '16px' }}>
            <button onClick={onBackToHistory} style={{
              display: 'flex', alignItems: 'center', padding: '8px 16px',
              borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
              background: 'rgba(255,255,255,0.04)', color: '#94a3b8',
              fontSize: '13px', fontWeight: '500', cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '6px' }}>
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
              </svg>
              Back to History
            </button>
          </div>
        )}
        {!isViewOnly && (
        <p style={styles.tagline} className="titler-tagline">
          {mode === 'url'
            ? 'Find the best keywords for your YouTube titles. Paste a video URL and we\'ll analyze the transcript to surface the most frequently used words and topics.'
            : 'Paste a blog post, script, or any text to analyze keywords, readability, power words, and more.'}
        </p>
        )}

        {!isViewOnly && (<>
        <div style={styles.searchContainer} className="titler-search-container">
          {/* Mode toggle */}
          <div style={styles.modeToggle}>
            <button
              onClick={() => switchMode('url')}
              style={mode === 'url' ? styles.modeButtonActive : styles.modeButton}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '6px' }}>
                <path d="M10 15l5.19-3L10 9v6m11.56-7.83c.13.47.22.97.22 1.52v9.62c0 .55-.09 1.05-.22 1.52-.12.47-.33.86-.61 1.16-.28.3-.63.52-1.07.65-.44.12-.96.19-1.57.19H5.69c-.61 0-1.13-.07-1.57-.19-.44-.13-.79-.35-1.07-.65-.28-.3-.49-.69-.61-1.16C2.31 19.36 2.22 18.86 2.22 18.31V8.69c0-.55.09-1.05.22-1.52.12-.47.33-.86.61-1.16.28-.3.63-.52 1.07-.65C4.56 5.24 5.08 5.17 5.69 5.17h12.62c.61 0 1.13.07 1.57.19.44.12.79.35 1.07.65.28.3.49.69.61 1.16z"/>
              </svg>
              YouTube URL
              {!canUseFeatures && <span style={styles.proBadge}>PRO</span>}
            </button>
            <button
              onClick={() => switchMode('text')}
              style={mode === 'text' ? styles.modeButtonActive : styles.modeButton}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ marginRight: '6px' }}>
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
              </svg>
              Paste Text
            </button>
          </div>

          {mode === 'url' ? (
            <div style={styles.searchBar} className="titler-search-bar">
              <svg style={styles.searchIcon} viewBox="0 0 24 24" fill="#6b7280">
                <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input
                type="text"
                value={url}
                onChange={e => setUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
                placeholder="Paste a YouTube URL..."
                style={styles.searchInput}
                className="titler-search-input"
                autoFocus
              />
              {url && (
                <button onClick={() => { setUrl(''); clearResults(); }} style={styles.clearButton}>
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="#94a3b8">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                  </svg>
                </button>
              )}
            </div>
          ) : (
            <div style={styles.textInputWrap}>
              <textarea
                value={manualText}
                onChange={e => { if (e.target.value.length <= 4000) setManualText(e.target.value); }}
                maxLength={4000}
                placeholder="Paste your blog post, script, or text here..."
                style={styles.textArea}
                className="titler-textarea"
                rows={6}
              />
              <div style={styles.textMeta}>
                <span style={{ ...styles.charInfo, color: manualText.length >= 4000 ? '#f87171' : manualText.length >= 3600 ? '#fbbf24' : '#64748b' }}>
                  {manualText.length.toLocaleString()} / 4,000 chars &middot; {manualText.trim() ? tokenize(manualText).length.toLocaleString() : 0} words
                </span>
                {manualText && (
                  <button onClick={() => { setManualText(''); clearResults(); }} style={styles.clearTextBtn}>Clear</button>
                )}
              </div>
            </div>
          )}

          <div style={styles.buttonRow}>
            <button onClick={handleAnalyze} disabled={!canAnalyze}
                    style={{
                      ...styles.analyzeButton,
                      ...(noCredits && mode === 'url' ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                    }} className="titler-analyze-btn">
              {loading ? 'Analyzing...' : (noCredits && mode === 'url') ? 'No Credits Remaining' : 'Analyze Keywords'}
            </button>
            {noCredits && mode === 'url' && (
              <div style={{ textAlign: 'center', marginTop: '8px' }}>
                <span style={{ fontSize: '13px', color: '#94a3b8' }}>
                  You're out of credits. Pro plan with unlimited access is coming soon.
                </span>
              </div>
            )}
            {mode === 'text' && user && (
              <div style={{ textAlign: 'center', marginTop: '10px', maxWidth: '480px' }}>
                <span style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>
                  {canUseFeatures
                    ? 'Uses 1 credit for full AI analysis (titles, sentiment, hooks & more). Basic keyword analysis is always free.'
                    : 'You\'re out of credits. Text analysis will run in basic mode (keywords & readability). Upgrade to Pro for unlimited AI analysis.'}
                </span>
              </div>
            )}
          </div>

          {/* Recent searches — only show before results */}
          {!hasResults && !loading && recentSearches.length > 0 && (
            <div style={{ marginTop: '20px', width: '100%' }} className="titler-recent">
              <div style={{ fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                {recentLabel}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {recentSearches.map(item => (
                  <button
                    key={item.id}
                    onClick={() => restoreFromHistory(item)}
                    className="titler-recent-item"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', borderRadius: '10px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      cursor: 'pointer', fontFamily: 'inherit',
                      textAlign: 'left', width: '100%',
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                  >
                    <img
                      src={`https://img.youtube.com/vi/${item.videoId}/default.jpg`}
                      alt=""
                      className="titler-recent-thumb"
                      style={{ width: '48px', height: '36px', borderRadius: '4px', objectFit: 'cover', flexShrink: 0 }}
                    />
                    <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                      <div className="titler-recent-title" style={{
                        fontSize: '13px', fontWeight: '500', color: '#cbd5e1',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {item.title}
                      </div>
                      {item.topKeywords?.length > 0 && (
                        <div className="titler-recent-keywords" style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {item.topKeywords.slice(0, 3).join(' · ')}
                        </div>
                      )}
                    </div>
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="#475569" style={{ flexShrink: 0 }}>
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"/>
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Loading screen — shows while fetching transcript / analyzing */}
        {loading && (
          <div style={styles.loadingScreen}>
            <div style={styles.loadingSpinner} className="titler-loading-spinner" />
            <p style={styles.loadingTitle}>Analyzing your video...</p>
            <p style={styles.loadingSubtext}>Fetching transcript and running keyword analysis</p>
            <div style={styles.loadingSteps}>
              <div style={styles.loadingStep}>
                <div style={{ ...styles.loadingStepDot, background: '#a78bfa' }} />
                <span style={styles.loadingStepText}>Extracting transcript</span>
              </div>
              <div style={styles.loadingStep}>
                <div style={{ ...styles.loadingStepDot, background: 'rgba(167,139,250,0.3)' }} />
                <span style={{ ...styles.loadingStepText, color: '#64748b' }}>Analyzing keywords</span>
              </div>
              <div style={styles.loadingStep}>
                <div style={{ ...styles.loadingStepDot, background: 'rgba(167,139,250,0.3)' }} />
                <span style={{ ...styles.loadingStepText, color: '#64748b' }}>Generating insights</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={styles.error} className="titler-error">
            <div>{error}</div>
            {(error.toLowerCase().includes('no transcript') || error.toLowerCase().includes('no caption') || error.toLowerCase().includes('not available')) && (
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#94a3b8', lineHeight: '1.6' }}>
                <strong style={{ color: '#e2e8f0' }}>This video doesn't have captions.</strong> This can happen when:
                <ul style={{ margin: '6px 0 8px', paddingLeft: '18px' }}>
                  <li>The creator disabled captions</li>
                  <li>The video is too short for auto-captions</li>
                  <li>The video has no speech (music, visuals only)</li>
                </ul>
                <strong style={{ color: '#e2e8f0' }}>Try instead:</strong> Switch to "Paste Text" mode and paste the script or transcript manually.
              </div>
            )}
          </div>
        )}

        {limitReached && (
          <div style={styles.limitBanner}>
            <svg viewBox="0 0 24 24" width="20" height="20" fill="#fbbf24" style={{ flexShrink: 0 }}>
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <div>
              <strong>Request limit reached</strong>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#94a3b8' }}>
                You've used all your available credits. Contact support or upgrade to continue analyzing videos.
              </p>
            </div>
          </div>
        )}
        </>)}

        {keywords && (
          <div style={styles.results} className="titler-results">
            {/* Video Context Card */}
            {videoInfo && (
              <div style={styles.videoCard} className="titler-video-card">
                <a href={`https://www.youtube.com/watch?v=${videoInfo.videoId}`}
                   target="_blank" rel="noopener noreferrer" style={styles.thumbLink}>
                  <img
                    src={`https://img.youtube.com/vi/${videoInfo.videoId}/hqdefault.jpg`}
                    alt="Video thumbnail"
                    style={styles.thumbnail}
                    className="titler-thumbnail"
                  />
                </a>
                <div style={styles.videoMeta}>
                  {videoInfo.title ? (
                    <>
                      <span style={styles.videoLabel}>Current Title</span>
                      <h3 style={styles.videoTitle}>{videoInfo.title}</h3>
                    </>
                  ) : (
                    <h3 style={styles.videoTitle}>Video {videoInfo.videoId}</h3>
                  )}
                  <div style={styles.videoDetails}>
                    {videoInfo.duration && (
                      <span style={styles.videoDetail}>Duration: {videoInfo.duration}</span>
                    )}
                    <span style={styles.videoDetail}>
                      Words: {transcript ? tokenize(transcript).length.toLocaleString() : '—'}
                    </span>
                    <span style={styles.videoDetail}>
                      ID: {videoInfo.videoId}
                    </span>
                    {transcriptSource && (
                      <span style={{
                        ...styles.videoDetail,
                        background: transcriptSource === 'whisper' ? 'rgba(168,85,247,0.15)' : 'rgba(74,222,128,0.15)',
                        color: transcriptSource === 'whisper' ? '#c084fc' : '#4ade80',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                      }}>
                        {transcriptSource === 'whisper' ? 'Whisper AI' : 'Captions'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Title Optimizer — AI titles separate from template titles */}
            {(() => {
              const displayAi = showProFeatures ? aiResult : MOCK_AI_RESULT;
              return (
                <>
                  <TitleOptimizer
                    aiTitles={displayAi?.titles || []}
                    isPro={showProFeatures}
                    signedIn={!!user}
                  />

                  {/* AI loading — only for users with access */}
                  {aiLoading && showProFeatures && (
                    <div style={{ ...styles.section, marginTop: '24px', textAlign: 'center' }}>
                      <div style={styles.aiLoadingDots}>
                        <span style={styles.aiLoadingDot} />
                        <span style={{ ...styles.aiLoadingDot, animationDelay: '0.2s' }} />
                        <span style={{ ...styles.aiLoadingDot, animationDelay: '0.4s' }} />
                      </div>
                      <p style={{ margin: '8px 0 0', color: '#a78bfa', fontSize: '14px' }}>
                        AI is analyzing your video...
                      </p>
                    </div>
                  )}

                  {/* AI sections — real data for users with access, mock+blurred for everyone else */}
                  {(showProFeatures ? aiResult : MOCK_AI_RESULT) && (
                    <ProGate locked={!showProFeatures} label="AI Analysis" signedIn={!!user}>
                    <div className="titler-grid" style={{ marginTop: '24px' }}>
                      {/* AI Summary */}
                      {displayAi.summary && (
                        <div style={{ ...styles.aiCard, gridColumn: '1 / -1' }} className="titler-card">
                          <div style={styles.aiLabel}>AI</div>
                          <h3 style={styles.sectionTitle}>Video Summary</h3>
                          <p style={styles.aiSummaryText}>{displayAi.summary}</p>
                        </div>
                      )}

                      {/* AI Sentiment with visual meter */}
                      {displayAi.sentiment && (
                        <div style={styles.aiCard} className="titler-card">
                          <div style={styles.aiLabel}>AI</div>
                          <h3 style={styles.sectionTitle}>Sentiment Analysis</h3>
                          <div style={styles.sentimentRow}>
                            <div style={styles.sentimentChip}>
                              <span style={styles.sentimentIcon}>
                                {displayAi.sentiment.overall === 'positive' ? '😊' :
                                 displayAi.sentiment.overall === 'negative' ? '😔' :
                                 displayAi.sentiment.overall === 'mixed' ? '🤔' : '😐'}
                              </span>
                              <span style={styles.sentimentText}>
                                {displayAi.sentiment.overall?.charAt(0).toUpperCase() + displayAi.sentiment.overall?.slice(1)}
                              </span>
                            </div>
                            {displayAi.sentiment.tone && (
                              <span style={styles.toneTag}>{displayAi.sentiment.tone}</span>
                            )}
                            {displayAi.sentiment.energy && (
                              <span style={styles.energyTag}>
                                {displayAi.sentiment.energy === 'high' ? '⚡' : displayAi.sentiment.energy === 'low' ? '🌙' : '☀️'}
                                {' '}{displayAi.sentiment.energy} energy
                              </span>
                            )}
                          </div>
                          {displayAi.sentiment.score != null && (
                            <div style={styles.meterWrap}>
                              <div style={styles.meterTrack}>
                                <div style={{
                                  ...styles.meterFill,
                                  width: `${displayAi.sentiment.score}%`,
                                  background: displayAi.sentiment.score >= 60
                                    ? 'linear-gradient(90deg, #4ade80, #22c55e)'
                                    : displayAi.sentiment.score >= 40
                                      ? 'linear-gradient(90deg, #fbbf24, #f59e0b)'
                                      : 'linear-gradient(90deg, #f87171, #ef4444)',
                                }} />
                              </div>
                              <div style={styles.meterLabels}>
                                <span>Negative</span>
                                <span>Neutral</span>
                                <span>Positive</span>
                              </div>
                            </div>
                          )}
                          {displayAi.sentiment.summary && (
                            <p style={styles.sentimentSummary}>{displayAi.sentiment.summary}</p>
                          )}
                        </div>
                      )}

                      {/* AI Quotes */}
                      {displayAi.quotes?.length > 0 && (
                        <div style={styles.aiCard} className="titler-card">
                          <div style={styles.aiLabel}>AI</div>
                          <h3 style={styles.sectionTitle}>Best Quotes</h3>
                          <p style={styles.hint}>Most shareable lines from the video</p>
                          <div style={styles.quotableList}>
                            {displayAi.quotes.map((q, i) => (
                              <div key={i} style={{ ...styles.aiQuoteItem, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>"{q}"</span>
                                <CopyButton text={q} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Hooks */}
                      {displayAi.hooks?.length > 0 && (
                        <div style={{ ...styles.aiCard, gridColumn: '1 / -1' }} className="titler-card">
                          <div style={styles.aiLabel}>AI</div>
                          <h3 style={styles.sectionTitle}>Opening Hook Suggestions</h3>
                          <p style={styles.hint}>Strong opening lines to grab attention in the first 5 seconds</p>
                          <div style={styles.hookList}>
                            {displayAi.hooks.map((h, i) => (
                              <div key={i} style={styles.hookItem}>
                                <span style={styles.hookNumber}>{i + 1}</span>
                                <span style={styles.hookText}>{h}</span>
                                <CopyButton text={h} />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AI Tags */}
                      {displayAi.tags?.length > 0 && (
                        <div style={{ ...styles.aiCard, gridColumn: '1 / -1' }} className="titler-card">
                          <div style={styles.aiLabel}>AI</div>
                          <h3 style={styles.sectionTitle}>SEO Tags</h3>
                          <p style={styles.hint}>Suggested tags for YouTube SEO — click to copy all</p>
                          <div style={styles.tagsWrap}>
                            {displayAi.tags.map((tag, i) => (
                              <span key={i} style={styles.seoTag}>
                                {tag}
                                <CopyButton text={tag} />
                              </span>
                            ))}
                          </div>
                          <button
                            onClick={() => navigator.clipboard.writeText(displayAi.tags.join(', '))}
                            style={styles.copyAllBtn}
                          >
                            Copy All Tags
                          </button>
                        </div>
                      )}
                    </div>
                    </ProGate>
                  )}
                  {/* Description Generator */}
                  <ProGate locked={!showProFeatures} label="Description Generator" signedIn={!!user}>
                    <div style={{ ...styles.section, marginTop: '24px' }}>
                      <h3 style={styles.sectionTitle}>YouTube Description Generator</h3>
                      <p style={styles.hint}>Auto-generated description based on your analysis data</p>
                      <div style={{ position: 'relative' }}>
                        <pre style={{ ...styles.transcript, maxHeight: '300px' }}>{generateDescription(keywords, videoInfo, showProFeatures ? aiResult : null)}</pre>
                        <div style={{ position: 'absolute', top: '8px', right: '8px' }}>
                          <CopyButton text={generateDescription(keywords, videoInfo, showProFeatures ? aiResult : null)} />
                        </div>
                      </div>
                    </div>
                  </ProGate>

                </>
              );
            })()}

            {/* Two-column grid for smaller cards */}
            <div className="titler-grid" style={{ marginTop: '24px' }}>
              {/* Readability */}
              <div style={styles.section} className="titler-card">
                <h3 style={styles.sectionTitle}>Transcript Readability</h3>
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 12px 0', lineHeight: '1.4' }}>
                  How easy your video's language is to follow. Simpler language reaches a wider audience — top YouTubers typically aim for a grade level of 6–8 and an ease score above 60.
                </p>
                <div style={styles.readabilityRow}>
                  <div style={styles.readabilityStat}>
                    <span style={styles.readabilityValue}>{keywords.readability.ease}</span>
                    <span style={styles.readabilityLabel}>Reading Ease</span>
                    <span style={styles.readabilityHint}>
                      {keywords.readability.ease >= 60 ? 'Easy — conversational, accessible to most viewers' : keywords.readability.ease >= 30 ? 'Moderate — some viewers may lose focus' : 'Difficult — may alienate casual viewers'}
                    </span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px', textAlign: 'center' }}>
                      Scale: 0 (hardest) → 100 (easiest)
                    </span>
                  </div>
                  <div style={styles.readabilityStat}>
                    <span style={styles.readabilityValue}>{keywords.readability.grade}</span>
                    <span style={styles.readabilityLabel}>Grade Level</span>
                    <span style={styles.readabilityHint}>
                      {keywords.readability.grade <= 6 ? 'Elementary — great for broad appeal' : keywords.readability.grade <= 10 ? 'High school — good for most content' : 'College+ — best for expert audiences'}
                    </span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '4px', textAlign: 'center' }}>
                      U.S. school grade needed to understand
                    </span>
                  </div>
                </div>
              </div>

              {/* Power Words */}
              {keywords.powerWords.length > 0 && (
                <div style={styles.section} className="titler-card">
                  <h3 style={styles.sectionTitle}>Power Words Detected</h3>
                  <p style={styles.hint}>Emotional/click-driving words found in the transcript</p>
                  <div style={styles.powerWordList}>
                    {keywords.powerWords.map(({ word, count }) => (
                      <span key={word} style={styles.powerWordTag}>
                        {word} <span style={styles.powerWordCount}>{count}</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Questions */}
              {keywords.questions.length > 0 && (
                <div style={styles.section} className="titler-card">
                  <h3 style={styles.sectionTitle}>Questions From the Video</h3>
                  <p style={styles.hint}>Questions make strong title hooks</p>
                  <ul style={styles.summaryList}>
                    {keywords.questions.map((q, i) => (
                      <li key={i} style={styles.questionItem}>{q}?</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Quotable Moments — only show if AI quotes aren't available */}
              {!aiResult?.quotes?.length && keywords.quotables.length > 0 && (
                <div style={styles.section} className="titler-card">
                  <h3 style={styles.sectionTitle}>Quotable Moments</h3>
                  <p style={styles.hint}>Short, punchy phrases with high keyword density</p>
                  <div style={styles.quotableList}>
                    {keywords.quotables.map((q, i) => (
                      <div key={i} style={styles.quotableItem}>"{q}"</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Summary — only show keyword-based if AI summary isn't available */}
            {!aiResult?.summary && keywords.summary.length > 0 && (
              <div style={{ ...styles.summarySection, marginTop: '24px' }}>
                <h3 style={styles.sectionTitle}>What This Video Is About</h3>
                <ul style={styles.summaryList}>
                  {keywords.summary.map((s, i) => (
                    <li key={i} style={styles.summaryItem}>{s}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Topic Timeline — full width */}
            <div style={{ marginTop: '24px' }}>
              <TopicTimeline timeline={keywords.timeline} />
            </div>

            {/* Charts in two-column grid */}
            <div className="titler-grid" style={{ marginTop: '24px' }}>
              {keywords.phrases.length > 0 && (
                <div style={styles.section} className="titler-card">
                  <KeywordChart keywords={keywords.phrases} title="Top Phrases" />
                </div>
              )}
              <div style={styles.section} className="titler-card">
                <KeywordChart keywords={keywords.singles} title="Top Keywords" />
              </div>
            </div>

            {transcript && (
              <div style={styles.transcriptSection}>
                <button onClick={() => setShowTranscript(!showTranscript)} style={styles.toggleButton}>
                  {showTranscript ? 'Hide Transcript' : 'Show Full Transcript'}
                </button>
                {showTranscript && (
                  <pre style={styles.transcript} className="titler-transcript">{transcript}</pre>
                )}
              </div>
            )}

          </div>
        )}

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    position: 'relative',
    color: '#e2e8f0',
    overflowX: 'hidden',
  },
  centeredLayout: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', minHeight: '100vh', padding: '60px 20px 0',
  },
  topLayout: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '100px 20px 40px',
  },
  logo: {
    fontSize: '72px', fontWeight: '800', marginBottom: '16px',
    letterSpacing: '-3px',
    background: 'linear-gradient(135deg, #a78bfa, #818cf8, #6366f1)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  tagline: {
    maxWidth: '480px', textAlign: 'center', color: '#94a3b8',
    fontSize: '15px', lineHeight: '1.6', marginBottom: '32px', marginTop: 0,
  },
  searchContainer: {
    width: '100%', maxWidth: '580px', display: 'flex',
    flexDirection: 'column', alignItems: 'center',
  },
  searchBar: {
    width: '100%', display: 'flex', alignItems: 'center',
    background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '16px', padding: '0 20px', height: '52px',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  searchIcon: { width: '20px', height: '20px', marginRight: '14px', flexShrink: 0 },
  searchInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: '15px',
    fontFamily: 'inherit', backgroundColor: 'transparent', color: '#e2e8f0',
    minWidth: 0,
  },
  clearButton: {
    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
    display: 'flex', alignItems: 'center', flexShrink: 0,
  },
  modeToggle: {
    display: 'flex', gap: '4px', marginBottom: '12px',
    background: 'rgba(255,255,255,0.04)', borderRadius: '10px',
    padding: '4px', width: '100%',
  },
  modeButton: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '8px 12px', borderRadius: '8px', border: 'none',
    background: 'transparent', color: '#64748b', fontSize: '13px',
    fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit',
    transition: 'color 0.15s, background 0.15s',
  },
  modeButtonActive: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '8px 12px', borderRadius: '8px', border: 'none',
    background: 'rgba(124,58,237,0.15)', color: '#c4b5fd', fontSize: '13px',
    fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
  },
  textInputWrap: {
    width: '100%',
  },
  textArea: {
    width: '100%', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '12px', padding: '14px 16px',
    background: 'rgba(255,255,255,0.07)', color: '#e2e8f0',
    fontSize: '14px', fontFamily: 'inherit', lineHeight: '1.6',
    resize: 'vertical', outline: 'none', minHeight: '100px',
    boxSizing: 'border-box',
  },
  textMeta: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginTop: '6px', padding: '0 4px',
  },
  charInfo: {
    fontSize: '12px', color: '#64748b',
  },
  clearTextBtn: {
    background: 'none', border: 'none', color: '#94a3b8',
    fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit',
    padding: '2px 6px',
  },
  buttonRow: {
    display: 'flex', gap: '12px', marginTop: '20px', width: '100%',
    justifyContent: 'center',
  },
  analyzeButton: {
    padding: '0 28px', height: '42px', border: 'none',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
    color: '#fff', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
    transition: 'opacity 0.2s',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  error: {
    maxWidth: '580px', width: '100%', marginTop: '20px',
    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '10px', padding: '12px 16px', color: '#fca5a5', fontSize: '14px',
  },
  limitBanner: {
    maxWidth: '580px', width: '100%', marginTop: '20px',
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)',
    borderRadius: '10px', padding: '16px', color: '#fbbf24', fontSize: '14px',
    fontWeight: '600',
  },
  results: {
    maxWidth: '960px', width: '100%', marginTop: '36px',
  },
  section: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px', padding: '20px 24px',
  },
  summarySection: {
    background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)',
    borderRadius: '12px', padding: '20px 24px',
  },
  sectionTitle: {
    margin: '0 0 8px', color: '#e2e8f0', fontSize: '16px', fontWeight: '600',
  },
  hint: {
    margin: '0 0 14px', color: '#64748b', fontSize: '13px',
  },
  summaryList: {
    margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: '10px',
  },
  summaryItem: {
    color: '#cbd5e1', fontSize: '14px', lineHeight: '1.6',
  },
  // Title Optimizer
  titleInputWrap: {
    display: 'flex', alignItems: 'center', gap: '12px',
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: '10px', padding: '0 16px', height: '44px',
  },
  titleInput: {
    flex: 1, border: 'none', outline: 'none', fontSize: '14px',
    fontFamily: 'inherit', backgroundColor: 'transparent', color: '#e2e8f0',
    minWidth: 0,
  },
  charCount: {
    fontSize: '13px', fontFamily: 'monospace', fontWeight: '600', flexShrink: 0,
  },
  truncationPreview: {
    marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px',
  },
  previewRow: {
    display: 'flex', alignItems: 'center', fontSize: '13px',
  },
  previewLabel: {
    width: '70px', color: '#64748b', flexShrink: 0,
  },
  previewText: {
    color: '#cbd5e1', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  titleSuggestions: {
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  titleSuggestion: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: 'rgba(124,58,237,0.1)', border: '1px solid rgba(124,58,237,0.2)',
    borderRadius: '8px', padding: '10px 14px', cursor: 'pointer',
    color: '#c4b5fd', fontSize: '13px', fontFamily: 'inherit', textAlign: 'left',
    transition: 'background 0.15s',
  },
  // Questions
  questionItem: {
    color: '#93c5fd', fontSize: '14px', lineHeight: '1.6',
  },
  // Quotables
  quotableList: {
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  quotableItem: {
    color: '#fbbf24', fontSize: '14px', lineHeight: '1.6',
    fontStyle: 'italic', padding: '8px 14px',
    background: 'rgba(251,191,36,0.06)', borderRadius: '8px',
    borderLeft: '3px solid rgba(251,191,36,0.3)',
  },
  // Power words
  powerWordList: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
  },
  powerWordTag: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '20px', padding: '6px 14px',
    color: '#fca5a5', fontSize: '13px', fontWeight: '600',
  },
  powerWordCount: {
    fontSize: '11px', color: '#94a3b8', fontWeight: '400',
  },
  // Readability
  readabilityRow: {
    display: 'flex', gap: '24px', marginTop: '8px',
  },
  readabilityStat: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    flex: 1, padding: '16px', background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
  },
  readabilityValue: {
    fontSize: '32px', fontWeight: '700', color: '#a78bfa',
  },
  readabilityLabel: {
    fontSize: '13px', color: '#94a3b8', marginTop: '4px',
  },
  readabilityHint: {
    fontSize: '11px', color: '#94a3b8', marginTop: '4px', textAlign: 'center',
  },
  // Timeline
  timelineGrid: {
    display: 'flex', flexDirection: 'column', gap: '4px',
  },
  timelineHeaderRow: {
    display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px',
  },
  timelineHeader: {
    flex: 1, textAlign: 'center', fontSize: '11px', color: '#64748b',
  },
  timelineRow: {
    display: 'flex', alignItems: 'center', gap: '4px',
  },
  timelineKeyword: {
    width: '90px', fontSize: '13px', color: '#c4b5fd', fontWeight: '600',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0,
  },
  timelineCell: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '32px', position: 'relative',
  },
  timelineDot: {
    width: '24px', height: '24px', borderRadius: '50%',
    background: 'linear-gradient(135deg, #7c3aed, #818cf8)',
  },
  timelineCellCount: {
    position: 'absolute', fontSize: '10px', color: '#e2e8f0', fontWeight: '700',
  },
  // Video context card
  videoCard: {
    display: 'flex', gap: '20px', alignItems: 'flex-start',
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px', padding: '16px', marginBottom: '24px',
  },
  thumbLink: {
    flexShrink: 0, borderRadius: '8px', overflow: 'hidden',
    display: 'block', lineHeight: 0,
  },
  thumbnail: {
    width: '200px', height: 'auto', borderRadius: '8px',
    display: 'block',
  },
  videoMeta: {
    flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px',
  },
  videoLabel: {
    fontSize: '11px', fontWeight: '600', color: '#7c3aed',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  videoTitle: {
    margin: 0, fontSize: '16px', fontWeight: '600', color: '#e2e8f0',
    lineHeight: '1.4', wordBreak: 'break-word',
  },
  videoDetails: {
    display: 'flex', gap: '16px', flexWrap: 'wrap',
  },
  videoDetail: {
    fontSize: '13px', color: '#94a3b8',
  },
  // AI cards
  aiCard: {
    background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.2)',
    borderRadius: '12px', padding: '20px 24px', position: 'relative',
  },
  aiLabel: {
    position: 'absolute', top: '12px', right: '16px',
    fontSize: '10px', fontWeight: '700', color: '#10b981',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    background: 'rgba(16, 185, 129, 0.12)', padding: '3px 8px', borderRadius: '4px',
  },
  sentimentRow: {
    display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '8px',
  },
  sentimentChip: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: 'rgba(255,255,255,0.06)', borderRadius: '20px',
    padding: '6px 16px',
  },
  sentimentIcon: { fontSize: '20px' },
  sentimentText: { fontSize: '14px', fontWeight: '600', color: '#e2e8f0' },
  toneTag: {
    fontSize: '12px', color: '#94a3b8', fontStyle: 'italic',
    background: 'rgba(255,255,255,0.04)', padding: '4px 12px', borderRadius: '12px',
  },
  energyTag: {
    fontSize: '12px', color: '#fbbf24',
    background: 'rgba(251,191,36,0.08)', padding: '4px 12px', borderRadius: '12px',
  },
  sentimentSummary: {
    margin: '12px 0 0', fontSize: '13px', color: '#94a3b8', lineHeight: '1.6',
  },
  aiQuoteItem: {
    color: '#6ee7b7', fontSize: '14px', lineHeight: '1.6',
    fontStyle: 'italic', padding: '8px 14px',
    background: 'rgba(16, 185, 129, 0.06)', borderRadius: '8px',
    borderLeft: '3px solid rgba(16, 185, 129, 0.3)',
  },
  hookList: {
    display: 'flex', flexDirection: 'column', gap: '10px',
  },
  hookItem: {
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    padding: '12px 16px', background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
  },
  hookNumber: {
    width: '24px', height: '24px', borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg, #059669, #10b981)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700', color: '#fff',
  },
  hookText: {
    fontSize: '14px', color: '#e2e8f0', lineHeight: '1.5',
  },
  // Copy button
  copyBtn: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '6px', padding: '6px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, transition: 'background 0.15s',
  },
  // Inline AI label
  inlineAiLabel: {
    fontSize: '10px', fontWeight: '700', color: '#10b981',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    background: 'rgba(16, 185, 129, 0.12)', padding: '2px 6px', borderRadius: '4px',
    flexShrink: 0,
  },
  aiTitleSuggestion: {
    background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.2)',
    borderLeft: '3px solid #4ade80',
    color: '#6ee7b7',
  },
  // Sentiment meter
  meterWrap: {
    marginTop: '16px',
  },
  meterTrack: {
    width: '100%', height: '8px', borderRadius: '4px',
    background: 'rgba(255,255,255,0.08)', overflow: 'hidden',
  },
  meterFill: {
    height: '100%', borderRadius: '4px', transition: 'width 0.6s ease',
  },
  meterLabels: {
    display: 'flex', justifyContent: 'space-between',
    fontSize: '10px', color: '#64748b', marginTop: '4px',
  },
  // AI Summary
  aiSummaryText: {
    margin: '8px 0 0', fontSize: '15px', color: '#cbd5e1',
    lineHeight: '1.7', letterSpacing: '0.01em',
  },
  // AI loading dots
  aiLoadingDots: {
    display: 'flex', justifyContent: 'center', gap: '6px',
  },
  aiLoadingDot: {
    width: '8px', height: '8px', borderRadius: '50%',
    background: '#a78bfa',
    animation: 'pulse 1s ease-in-out infinite',
  },
  // SEO Tags
  tagsWrap: {
    display: 'flex', flexWrap: 'wrap', gap: '8px',
  },
  seoTag: {
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)',
    borderRadius: '20px', padding: '6px 12px',
    color: '#6ee7b7', fontSize: '13px',
  },
  copyAllBtn: {
    marginTop: '12px', padding: '8px 18px',
    background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.25)',
    borderRadius: '8px', color: '#6ee7b7', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.15s',
  },
  // Existing
  transcriptSection: { marginTop: '28px', paddingBottom: '40px' },
  toggleButton: {
    padding: '8px 18px', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px',
    background: 'rgba(255,255,255,0.06)', cursor: 'pointer', fontSize: '13px',
    color: '#94a3b8', fontFamily: 'inherit',
  },
  transcript: {
    marginTop: '12px', padding: '20px', backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px', fontSize: '13px',
    lineHeight: '1.7', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    maxHeight: '400px', overflow: 'auto', color: '#cbd5e1',
  },
  proBadge: {
    marginLeft: '6px', padding: '1px 6px', borderRadius: '4px',
    background: 'rgba(251,191,36,0.2)', color: '#fbbf24',
    fontSize: '9px', fontWeight: '700', letterSpacing: '0.5px',
  },
  // Loading screen
  loadingScreen: {
    maxWidth: '480px', width: '100%', marginTop: '40px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '40px 20px',
  },
  loadingSpinner: {
    width: '48px', height: '48px', borderRadius: '50%',
    border: '3px solid rgba(124,58,237,0.15)',
    borderTopColor: '#a78bfa',
    animation: 'spin 0.8s linear infinite',
  },
  loadingTitle: {
    margin: '20px 0 6px', fontSize: '17px', fontWeight: '600',
    color: '#e2e8f0',
  },
  loadingSubtext: {
    margin: 0, fontSize: '13px', color: '#64748b',
  },
  loadingSteps: {
    display: 'flex', flexDirection: 'column', gap: '12px',
    marginTop: '28px', width: '100%', maxWidth: '260px',
  },
  loadingStep: {
    display: 'flex', alignItems: 'center', gap: '10px',
  },
  loadingStepDot: {
    width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
  },
  loadingStepText: {
    fontSize: '13px', color: '#cbd5e1',
  },
};

export default YouTubeAnalyzer;
